import * as React from 'react';

import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { useState } from 'react';
import Page from '../components/Page';
import Container from '../components/Container';
import IndexLayout from '../layouts';
import { PageHeader } from '../components/PageHeader';
import { GammaSection } from '../components/GammaSection';
import { PriceChangePercent, StyledPriceNegativeNumber, StyledPricePositiveNumber, StyleLastUpdatedBlock } from '../components/TrendsTable';
import { Chart } from '../components/Chart';
import { abbreviateNumber, abbreviateVolume, addCommas } from '../utils/numbers';
import { FailuresChart } from '../components/FailuresChart';
import Tables from '../components/Tables';

interface TickerTemplateProps {
  pageContext: any;
}

interface PostsProps {
  heading: string;
  flair: string;
  ticker: string;
  posts: any[];
}

const StyledPostsContainer = styled.div`
  background: ${p => p.theme.colors.card};
  padding: 5px 5px 0 5px;
  border: 1px solid ${p => p.theme.colors.horizontalRule};
  border-radius: 5px;
  font-size: 0.85em;

  box-shadow: rgba(0, 0, 0, 0.07) 0px 4px 20px;
`;

const StyledPostContainer = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 5px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${p => p.theme.colors.horizontalRule};

  :last-child {
    border-bottom: none;
    margin-bottom: 0px;
  }
`;

const StyledPostContent = styled.div`
  margin: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex-grow: 1;
`;

const StyledPostThumbnail = styled.div`
  flex-shrink: 0;
  width: 85px;
  margin-top: 7px;
  margin-right: 5px;
  overflow: hidden;
`;

const StyledPostAnchor = styled.a`
  font-size: 1.3em;
`;

const StyledHeader = styled.h1`
  width: 100%;
  border-bottom: 2px solid ${p => p.theme.colors.primary};
  margin-top: 40px;
  margin-bottom: 15px;
  display: flex;

  color: ${p => p.theme.colors.primary};
  transition: ${p => p.theme.colorModeTransition};
`;

const StyledHeaderName = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledHeaderTicker = styled.div`
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 220px;
  margin-left: 5px;
`;

const StyledPostText = styled.p`
  margin-bottom: 2px;
  max-width: 100%;
  max-height: 64px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
  padding: 2px 0;
`;

const StyledPostCommentsLink = styled.a`
  font-weight: bold;
  font-color: #999;
  color: #777;
`;

const StyledCommentList = styled.ul`
  margin-top: 7px;
  margin-bottom: 5px;
  padding-left: 20px;

  li {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledSummary = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-shart;
`;

const StyledInfoBlock = styled.div`
  display: flex;
  flex: 0;
  flex-basis: 200px;
`;

const StyledAddressLine = styled.div`
  display: block;
`;

const StyledAddressLineTopMargin = styled.div`
  display: block;
  margin-top: 15px;
`;

const StyledBlurb = styled.div`
  flex: 1;
  flex-grow: 1;
  font-size: 0.875em;
  margin-left: 10px;
`;

const StyledLoadMore = styled.div`
  margin: 20px;
  text-align: center;
  font-weight: bold;
  color: ${p => p.theme.colors.accent};
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`;

const StyledLoadMoreDesc = styled.span`
  color: ${p => p.theme.colors.accent};
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;

const StyledUnusualHeading = styled(Tables.HeadCell)`
  text-align: center;
`;

const StyledUnusualCell = styled(Tables.Cell)`
  font-size: 0.9em;
  text-align: right;
`;

const StyledUnusualCellLeft = styled(Tables.Cell)`
  font-size: 0.9em;
`;

const StyledNewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  background: ${p => p.theme.colors.card};
  padding: 5px 5px 0 5px;
  border: 1px solid ${p => p.theme.colors.horizontalRule};
  border-radius: 5px;

  box-shadow: rgba(0, 0, 0, 0.07) 0px 4px 20px;

  margin-bottom: 30px;
`;

const StyledNewsItem = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  max-width: 100%;

  margin-bottom: 7px;
  padding-bottom: 3px;
  border-bottom: 1px solid ${p => p.theme.colors.horizontalRule};

  :last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 2px;
  }
`;

const StyledNewsItemContent = styled.div`
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledNewsThumb = styled.div`
  flex-shrink: 0;
  img {
    max-height: 3.8em;
  }
  margin-right: 5px;
`;

const StyledNewsSource = styled.div`
  font-size: 0.77em;
  color: ${p => p.theme.colors.primary};
  opacity: 0.66;
`;

const StyledNewsTitle = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;

  a {
    color: ${p => p.theme.colors.primary};
  }
`;

const StyledNewsText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
  color: ${p => p.theme.colors.primary};
`;

const StyledStats = styled.div`
  flex-wrap: wrap;
  display: flex;
  justify-content: space-between;
  margin-left: -10px;

  div {
    margin-left: 10px;
    margin-bottom: 10px;
  }
`;

const StyledStatsContainer = styled.div`
  flex-grow: 1;
  overflow: auto;

  border: 1px solid ${p => p.theme.colors.horizontalRule};
  border-radius: 5px;
  box-shadow: rgba(0, 0, 0, 0.07) 0px 4px 20px;

  td {
    border-color: ${p => p.theme.colors.horizontalRule};
  }
`;

const StyledStatsLabelCell = styled(Tables.Cell)`
  font-size: 13px;
`;

const StyledStatsDiffCell = styled(Tables.Cell)`
  font-size: 12px;
`;

const StyledStatsLabelValueCell = styled(Tables.Cell)`
  font-size: 12.5px;
`;

const StyledStatsLabelValueSubtleCell = styled(Tables.Cell)`
  font-size: 12.5px;
  color: ${p => p.theme.colors.grey};
`;

const StyledStatsValueCell = styled(Tables.Cell)`
  color: ${p => p.theme.colors.valueText};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const StyledStatsValueRightCell = styled(Tables.Cell)`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-align: right;
`;

function PostCommentItem({ comment }) {
  return (
    <li>
      ({comment.votes} points{comment.replyCount > 1 && `, ${comment.replyCount} replies`}){' '}
      <a href={`https://www.reddit.com${comment.permalink}`}>{comment.text}</a>
    </li>
  );
}

function Post({ post }) {
  return (
    <>
      <StyledPostContainer>
        {post.thumbnailUrl && post.thumbnailUrl !== 'self' && post.thumbnailUrl !== 'default' && (
          <StyledPostThumbnail>
            <a href={post.url}>
              <img src={post.thumbnailUrl} alt="thumbnail" />
            </a>
          </StyledPostThumbnail>
        )}
        <StyledPostContent>
          <StyledPostAnchor href={`https://www.reddit.com${post.permalink}`} title={post.title}>
            {post.title}
          </StyledPostAnchor>
          {!!post.selfText && (
            <StyledPostText>
              {post.selfText} {post.selfText.length >= 200 && <a href={`https://www.reddit.com${post.permalink}`}>Read More</a>}
            </StyledPostText>
          )}
          <div>
            <StyledPostCommentsLink href={`https://www.reddit.com${post.permalink}`}>
              {addCommas(post.commentCount)} comments | {addCommas(post.votes)} votes
            </StyledPostCommentsLink>
          </div>
          {post.topComments && post.topComments.length > 0 && (
            <StyledCommentList>
              {post.topComments.map(c => (
                <PostCommentItem key={c.permalink} comment={c} />
              ))}
            </StyledCommentList>
          )}
        </StyledPostContent>
      </StyledPostContainer>
    </>
  );
}

function Posts({ posts }) {
  const [showAmount, setShowAmount] = useState(Math.min(7, posts.length));

  return (
    <StyledPostsContainer>
      <>
        {posts
          .filter((t, index) => index < showAmount)
          .map(t => (
            <Post key={t.permalink} post={t} />
          ))}
        {showAmount < posts.length && (
          <StyledLoadMore
            onClick={() => {
              setShowAmount(amount => Math.min(amount + 7, posts.length));
            }}
          >
            Load More ({addCommas(posts.length - showAmount)} remaining)
          </StyledLoadMore>
        )}
      </>
    </StyledPostsContainer>
  );
}

function WebsiteLink({ url = '' }) {
  if (!url) return null;
  const index = url.indexOf('://');
  const displayUrl = index > 0 ? url.substr(index + 3) : url;
  return <a href={url}>{displayUrl}</a>;
}

const descriptionShowLength = 500;

function VolumeDifference({ value = 0, prev = 0 }) {
  if (value === prev) return null;
  const diff = Math.round((value - prev) * 100) / 100;
  const percent = Math.floor((diff / prev) * 10000) / 100;
  if (percent === 0) return null;

  const decimals = percent < 100 ? 2 : percent < 1000 ? 1 : 0;

  return (
    <>
      {value > prev ? (
        <>
          <StyledPricePositiveNumber title={`today's volume is ${percent.toFixed(decimals)}% (${abbreviateVolume(value - prev)}) higher`}>
            +{percent.toFixed(decimals)}%
          </StyledPricePositiveNumber>{' '}
          <span style={{ opacity: '0.6' }}>higher today</span>
        </>
      ) : (
        <>
          <StyledPriceNegativeNumber title={`today's volume is ${percent.toFixed(decimals)}% (${abbreviateVolume(prev - value)}) lower`}>
            {percent.toFixed(decimals)}%
          </StyledPriceNegativeNumber>{' '}
          <span style={{ opacity: '0.6' }}>lower today</span>
        </>
      )}
    </>
  );
}

function PreviousVolumeAndDifferenceCell({ label = '', value = 0, prev = 0 }) {
  if (!value || !prev)
    return (
      <tr>
        <StyledStatsLabelCell>{label}</StyledStatsLabelCell>
        <StyledStatsValueCell>N/A</StyledStatsValueCell>
        <StyledStatsLabelCell>&nbsp;</StyledStatsLabelCell>
      </tr>
    );
  return (
    <tr>
      <StyledStatsLabelCell>{label}</StyledStatsLabelCell>
      <StyledStatsValueCell colSpan={2} title={addCommas(prev)}>
        {abbreviateVolume(prev)}
      </StyledStatsValueCell>
    </tr>
  );
}

function StatsTable({ stats, history }) {
  const data = history?.data;
  let dayLow = 0;
  let dayHigh = 0;
  let decimals = 2;

  if (data && data.length > 0) {
    const last = data[0];
    dayLow = last.low;
    dayHigh = last.high;
    decimals = data.some(i => i.close > 0 && i.close < 0.01) ? 4 : 2;
  }

  return (
    <StyledStatsContainer>
      <table>
        <tbody>
          <tr>
            <StyledStatsLabelCell>Price</StyledStatsLabelCell>
            <StyledStatsValueCell>
              {stats?.currencySymbol} {stats?.price?.toFixed(decimals)}
            </StyledStatsValueCell>
            <StyledStatsLabelValueCell>
              <PriceChangePercent value={stats?.price} prev={stats?.previousClosingPrice} />
            </StyledStatsLabelValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Day's Range</StyledStatsLabelCell>
            <StyledStatsValueCell>
              {stats?.currencySymbol} {dayLow.toFixed(decimals)} - {dayHigh.toFixed(decimals)}
            </StyledStatsValueCell>
            <StyledStatsLabelValueCell>&nbsp;</StyledStatsLabelValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Previous Close</StyledStatsLabelCell>
            <StyledStatsValueCell>
              {stats?.currencySymbol} {stats?.previousClosingPrice?.toFixed(decimals)}
            </StyledStatsValueCell>
            <StyledStatsLabelCell />
          </tr>
          <tr>
            <StyledStatsLabelCell>Market Cap</StyledStatsLabelCell>
            <StyledStatsValueCell>
              {stats?.currencySymbol} {stats?.marketCap ?? 'N/A'} {stats?.marketCap && stats?.currency}
            </StyledStatsValueCell>
            <StyledStatsLabelCell />
          </tr>
          <tr>
            <StyledStatsLabelCell>Short Interest %</StyledStatsLabelCell>
            <StyledStatsValueCell>{stats?.shortInterestPercent ? `${stats.shortInterestPercent} %` : 'N/A'}</StyledStatsValueCell>
            <StyledStatsLabelValueSubtleCell>
              {stats?.shortAsOf && `As of ${stats.shortAsOf.substr(0, stats.shortAsOf.indexOf('202') + 4)}`}
            </StyledStatsLabelValueSubtleCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Float</StyledStatsLabelCell>
            <StyledStatsValueCell>{stats?.float ?? 'N/A'}</StyledStatsValueCell>
            <StyledStatsLabelCell />
          </tr>
          <tr>
            <StyledStatsLabelCell>Outstanding</StyledStatsLabelCell>
            <StyledStatsValueCell>{stats?.outstanding ?? 'N/A'}</StyledStatsValueCell>
            <StyledStatsLabelCell />
          </tr>
          {/* <tr>
            <StyledStatsLabelCell>&nbsp;</StyledStatsLabelCell>
            <StyledStatsLabelCell />
            <StyledStatsLabelCell />
          </tr> */}
        </tbody>
      </table>
    </StyledStatsContainer>
  );
}

function InfoRatingsTable({ ratings }) {
  function RatingRow({ name, value, percent = 0 }) {
    return (
      <tr>
        <StyledStatsLabelCell>{name}</StyledStatsLabelCell>
        <StyledStatsValueCell>{percent ? `${percent.toFixed(2)} %` : 'N/A'}</StyledStatsValueCell>
        <StyledStatsLabelValueCell>
          <RatingsRecommendation value={value} />
        </StyledStatsLabelValueCell>
      </tr>
    );
  }

  return (
    <StyledStatsContainer>
      <table>
        <tbody>
          <tr>
            <StyledStatsLabelCell>Rating</StyledStatsLabelCell>
            <StyledStatsValueCell>{ratings.rating}</StyledStatsValueCell>
            <StyledStatsLabelValueCell>
              <RatingsRecommendation value={ratings.ratingRecommendation} />
            </StyledStatsLabelValueCell>
          </tr>
          <RatingRow name="DCF" value={ratings.ratingDetailsDCFRecommendation} />
          <RatingRow name="Debt/Equity" percent={ratings.dePercent} value={ratings.ratingDetailsDERecommendation} />
          <RatingRow name="ROE" percent={ratings.roePercent} value={ratings.ratingDetailsROERecommendation} />
          <RatingRow name="ROA" percent={ratings.roaPercent} value={ratings.ratingDetailsROARecommendation} />
          <RatingRow name="P/E" percent={ratings.pePercent} value={ratings.ratingDetailsPERecommendation} />
          <RatingRow name="P/B" percent={ratings.pbPercent} value={ratings.ratingDetailsPBRecommendation} />
        </tbody>
      </table>
    </StyledStatsContainer>
  );
}

function InfoVolumeTable({ data }) {
  const { stats, volume, history } = data;

  const hasHistoryVolumeData = history?.data && history.data.length > 0 && history.data[0].avgVolume20d;
  const relVol20d = hasHistoryVolumeData ? history.data[0].volume / history.data[0].avgVolume20d : 0;
  const relVol3m = hasHistoryVolumeData ? history.data[0].volume / history.data[0].avgVolume3m : 0;

  return (
    <StyledStatsContainer>
      <table>
        <tbody>
          <tr>
            <StyledStatsLabelCell>Exchange</StyledStatsLabelCell>
            <StyledStatsValueCell colSpan={2}>{data.exchangeName}</StyledStatsValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Industry</StyledStatsLabelCell>
            <StyledStatsValueCell colSpan={2}>{data.industry || 'N/A'}</StyledStatsValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Sector</StyledStatsLabelCell>
            <StyledStatsValueCell colSpan={2}>{data.sector || 'N/A'}</StyledStatsValueCell>
          </tr>
          <PreviousVolumeAndDifferenceCell label="Volume" value={volume?.volume} prev={volume?.volume} />
          <PreviousVolumeAndDifferenceCell label="Avg. Volume (20 day)" value={volume?.volume} prev={volume?.avgVolume20d} />
          <tr>
            <StyledStatsLabelCell>Rel. Volume (20 day)</StyledStatsLabelCell>
            <StyledStatsValueCell colSpan={2}>{relVol20d ? relVol20d.toFixed(2) : 'N/A'}</StyledStatsValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell>Rel. Volume (3 month)</StyledStatsLabelCell>
            <StyledStatsValueCell colSpan={2}>{relVol3m ? relVol3m.toFixed(2) : 'N/A'}</StyledStatsValueCell>
          </tr>
        </tbody>
      </table>
    </StyledStatsContainer>
  );
}

function RatingsRecommendation({ value = '' }) {
  const hasBuy = value.toLowerCase().indexOf('buy') >= 0;
  const hasSell = value.toLowerCase().indexOf('sell') >= 0;

  if (hasBuy) {
    return <StyledPricePositiveNumber>{value}</StyledPricePositiveNumber>;
  }
  if (hasSell) {
    return <StyledPriceNegativeNumber>{value}</StyledPriceNegativeNumber>;
  }
  return <>{value}</>;
}

function RatingsTable({ ratings }) {
  return (
    <>
      <h1>Ratings</h1>
      <Tables.Table>
        <Tables.Body>
          <tr>
            <StyledStatsLabelCell>Rating:</StyledStatsLabelCell>
            <StyledStatsValueCell>
              {ratings.rating}&nbsp; <RatingsRecommendation value={ratings.ratingRecommendation} />
            </StyledStatsValueCell>

            <StyledStatsLabelCell>P/E:</StyledStatsLabelCell>
            <StyledStatsValueCell>
              <RatingsRecommendation value={ratings.ratingDetailsPERecommendation} />
            </StyledStatsValueCell>
          </tr>
          <tr>
            <StyledStatsLabelCell colSpan={2}> </StyledStatsLabelCell>

            <StyledStatsLabelCell>ROA:</StyledStatsLabelCell>
            <StyledStatsValueCell>
              <RatingsRecommendation value={ratings.ratingDetailsROARecommendation} />
            </StyledStatsValueCell>
            <StyledStatsLabelCell>P/B:</StyledStatsLabelCell>
            <StyledStatsValueCell>
              <RatingsRecommendation value={ratings.ratingDetailsPBRecommendation} />
            </StyledStatsValueCell>
          </tr>
        </Tables.Body>
      </Tables.Table>
    </>
  );
}

const StyledEarningsCell = styled(Tables.Cell)`
  font-size: 13px;
`;

const StyledEarningsHeadCellRight = styled(Tables.HeadCell)`
  text-align: right;
`;

const StyledEarningsHeadCellRightNoLeftPadding = styled(StyledEarningsHeadCellRight)`
  padding-left: 0;
`;

const StyledEarningsCellRight = styled(StyledEarningsCell)`
  text-align: right;
`;

const StyledEarningsCellRightNoLeftPadding = styled(StyledEarningsCellRight)`
  padding-left: 0;
`;

const StyledNoValue = styled.span`
  opacity: 0.5;
  letter-spacing: 1px;
`;

function EarningsSection({ earnings }) {
  function NoValue() {
    return <StyledNoValue>--</StyledNoValue>;
  }

  function ActualValue({ estimated, actual, commas = false, abbreviated = false }) {
    if (!actual || !estimated) return <NoValue />;
    const displayValue = abbreviated ? abbreviateNumber(actual) : commas ? addCommas(actual) : actual.toFixed(2);
    const title = `${commas || abbreviated ? addCommas(actual) : actual}`;
    if (actual > estimated) {
      return <StyledPricePositiveNumber title={title}>{displayValue}</StyledPricePositiveNumber>;
    }
    if (actual < estimated) {
      return <StyledPriceNegativeNumber title={title}>{displayValue}</StyledPriceNegativeNumber>;
    }
    return <>{displayValue}</>;
  }

  function ChangePercent({ value = 0, estimated = 0 }) {
    if (value === estimated || !value || !estimated) return <NoValue />;
    const diff = Math.round((value - estimated) * 100) / 100;
    const percent = Math.floor((diff / estimated) * 10000) / 100;

    return (
      <>
        {value > estimated ? (
          <StyledPricePositiveNumber title={`Surprise ${percent}%`}>{Math.abs(percent)}%</StyledPricePositiveNumber>
        ) : (
          <StyledPriceNegativeNumber title={`Surprise ${percent}%`}>{percent}%</StyledPriceNegativeNumber>
        )}
      </>
    );
  }

  return (
    <>
      <h1>Earnings</h1>
      <Tables.Table>
        <Tables.Head>
          <tr>
            <Tables.HeadCell>Date</Tables.HeadCell>
            <Tables.HeadCell>Time</Tables.HeadCell>
            <StyledEarningsHeadCellRight>EPS Expected</StyledEarningsHeadCellRight>
            <StyledEarningsHeadCellRight>EPS Reported</StyledEarningsHeadCellRight>
            <StyledEarningsHeadCellRightNoLeftPadding>%</StyledEarningsHeadCellRightNoLeftPadding>
            <StyledEarningsHeadCellRight>Revenue Expected</StyledEarningsHeadCellRight>
            <StyledEarningsHeadCellRight>Revenue Reported</StyledEarningsHeadCellRight>
            <StyledEarningsHeadCellRightNoLeftPadding>%</StyledEarningsHeadCellRightNoLeftPadding>
          </tr>
        </Tables.Head>
        <Tables.Body>
          {earnings.map(i => (
            <tr key={i.date}>
              <StyledEarningsCell>{i.date}</StyledEarningsCell>
              <StyledEarningsCell>{i.time.toUpperCase()}</StyledEarningsCell>
              <StyledEarningsCellRight>{i.epsEstimated || <NoValue />}</StyledEarningsCellRight>
              <StyledEarningsCellRight>
                <ActualValue estimated={i.epsEstimated} actual={i.eps} />
              </StyledEarningsCellRight>
              <StyledEarningsCellRightNoLeftPadding>
                <ChangePercent value={i.eps} estimated={i.epsEstimated} />
              </StyledEarningsCellRightNoLeftPadding>
              <StyledEarningsCellRight title={`${i.revenueEstimated ? addCommas(i.revenueEstimated) : ''}`}>
                {i.revenueEstimated ? abbreviateNumber(i.revenueEstimated) : <NoValue />}
              </StyledEarningsCellRight>
              <StyledEarningsCellRight>
                <ActualValue estimated={i.revenueEstimated} actual={i.revenue} abbreviated />
              </StyledEarningsCellRight>
              <StyledEarningsCellRightNoLeftPadding>
                <ChangePercent value={i.revenue} estimated={i.revenueEstimated} />
              </StyledEarningsCellRightNoLeftPadding>
            </tr>
          ))}
        </Tables.Body>
      </Tables.Table>
    </>
  );
}

const StyledUnusualRow = styled.tr`
  &:nth-of-type(even) td {
    background: ${p => p.theme.colors.cardAlt} !important;
  }
`;

function TickerTemplate({ pageContext: { data, renderTime } }: TickerTemplateProps) {
  const [showFullDesc, setShowFullDesc] = useState(!!data.description && data.description.length < descriptionShowLength + 50);

  return (
    <IndexLayout>
      <Page>
        <Container>
          <Helmet title={`wsbdaily - ${data.name} (${data.symbol})`} />
          <PageHeader time={renderTime} logoOnly />
          {/* <h1>{data.markdownRemark.frontmatter.title}</h1> */}
          {/* eslint-disable-next-line react/no-danger */}
          {/* <div dangerouslySetInnerHTML={{ __html: data.markdownRemark.html }} /> */}
          <StyledHeader>
            <StyledHeaderName title={data.name}>{data.name}</StyledHeaderName>
            <StyledHeaderTicker>({data.symbol})</StyledHeaderTicker>
          </StyledHeader>
          {!!data.description && (
            <StyledSummary>
              <StyledInfoBlock>
                <div>
                  <StyledAddressLine>
                    <b>{data.name}</b>
                  </StyledAddressLine>
                  <StyledAddressLine>{data.address.line1}</StyledAddressLine>
                  <StyledAddressLine>{data.address.line2}</StyledAddressLine>
                  <StyledAddressLine>
                    {data.address.city}, {data.address.state} {data.address.zip}
                  </StyledAddressLine>
                  <StyledAddressLine>{data.address.country}</StyledAddressLine>
                  <StyledAddressLineTopMargin>
                    <a href={`tel:${data.address.phone}`}>{data.address.phone}</a>
                  </StyledAddressLineTopMargin>
                  <StyledAddressLineTopMargin>
                    <WebsiteLink url={data.website} />
                  </StyledAddressLineTopMargin>
                </div>
              </StyledInfoBlock>
              <StyledBlurb>
                {showFullDesc ? (
                  data.description
                ) : (
                  <>
                    {data.description.substr(0, descriptionShowLength).trim()}...{' '}
                    <StyledLoadMoreDesc onClick={() => setShowFullDesc(true)}>Read More</StyledLoadMoreDesc>
                  </>
                )}
              </StyledBlurb>
            </StyledSummary>
          )}
          <h1>Summary & Charts</h1>
          {/* <TopStatsTable data={data} /> */}
          <StyledStats>
            <StatsTable stats={data.stats} history={data.history} />
            <InfoVolumeTable data={data} />
            {!!data.rating && <InfoRatingsTable ratings={data.rating} />}
          </StyledStats>
          {data.history && data.history.data && data.history.data.length > 1 && <Chart data={data.history.data} />}
          {data.gammas && data.gammas.squeezes.length > 0 && <GammaSection gammas={data.gammas} />}
          {data.news && data.news.length > 0 && (
            <>
              <h1>News</h1>
              <p>
                The latest news about {data.name} ({data.symbol}).
              </p>
              <StyledNewsContainer>
                {data.news.map(news => (
                  <StyledNewsItem key={news.url}>
                    {!!news.image && (
                      <StyledNewsThumb>
                        <a href={news.url}>
                          <img src={news.image} alt="" />
                        </a>
                      </StyledNewsThumb>
                    )}
                    <StyledNewsItemContent>
                      <StyledNewsSource>
                        {news.site} | {news.publishedDate}
                      </StyledNewsSource>
                      <StyledNewsTitle title={news.title}>
                        <a href={news.url}>{news.title}</a>
                      </StyledNewsTitle>
                      <StyledNewsText title={news.text}>{news.text}</StyledNewsText>
                    </StyledNewsItemContent>
                  </StyledNewsItem>
                ))}
              </StyledNewsContainer>
            </>
          )}
          {data.earnings && data.earnings.length > 0 && <EarningsSection earnings={data.earnings} />}
          {data.dueDiligencePosts && data.dueDiligencePosts.length > 0 && (
            <>
              <h1>Due Diligences</h1>
              <p>
                These posts should include more in-depth research by the poster about the specific company, sector or trade idea. Including
                any sources and citations.
              </p>
              <Posts posts={data.dueDiligencePosts} />
            </>
          )}
          <h1>Top Discussions</h1>
          <p>These are the top discussions over the last 24-hours that mention the {data.symbol} stock ticker symbol.</p>
          <Posts posts={data.topPosts} />
          <p>
            <br />
          </p>
          {data.unusual && data.unusual.unusual.length > 0 && (
            <>
              <h1>Unusual Option Activity</h1>
              <p>
                Option contracts that are trading at a significantly higher volume relative to the contract's open interest. These might
                provide some insight into what "smart money" is doing with large volume orders.
              </p>
              <Tables.Table>
                <Tables.Head>
                  <tr>
                    <Tables.HeadCell title="Call gives you the right to buy at the Strike price and Put the right to sell">
                      Type
                    </Tables.HeadCell>
                    <StyledUnusualHeading title="The price at which the holder can buy (CALL option) or sell (PUT option) when the option is exercised">
                      Strike
                    </StyledUnusualHeading>
                    <StyledUnusualHeading title="The date the option expires">Exp Date</StyledUnusualHeading>
                    <StyledUnusualHeading title="The number of days until the option expires">DTE</StyledUnusualHeading>
                    <StyledUnusualHeading title="The bid price">Bid</StyledUnusualHeading>
                    <StyledUnusualHeading title="The ask price">Ask</StyledUnusualHeading>
                    <StyledUnusualHeading title="The last trade price">Last</StyledUnusualHeading>
                    <StyledUnusualHeading title="The total number of contracts traded in the day">Volume</StyledUnusualHeading>
                    <StyledUnusualHeading title="The total number of contracts that have been traded but not yet liquidated">
                      Open Interest
                    </StyledUnusualHeading>
                    <StyledUnusualHeading title="Higher activity indicates unusual activity">Vol/OI</StyledUnusualHeading>
                    <StyledUnusualHeading title="Implied Volatility, estimate of the volatility of the underlying stock over the period of the option">
                      IV
                    </StyledUnusualHeading>
                  </tr>
                </Tables.Head>
                <Tables.Body>
                  {data.unusual.unusual.map(u => (
                    <StyledUnusualRow key={u.contractName}>
                      <StyledUnusualCellLeft>{u.optionType}</StyledUnusualCellLeft>
                      <StyledUnusualCell>
                        {u.currencySymbol} {u.strike.toFixed(2)}
                      </StyledUnusualCell>
                      <StyledUnusualCell>{u.expires}</StyledUnusualCell>
                      <StyledUnusualCell>{u.daysToExpire}</StyledUnusualCell>
                      <StyledUnusualCell>
                        {u.currencySymbol} {u.bid.toFixed(2)}
                      </StyledUnusualCell>
                      <StyledUnusualCell>
                        {u.currencySymbol} {u.ask.toFixed(2)}
                      </StyledUnusualCell>
                      <StyledUnusualCell>
                        {u.currencySymbol} {u.lastPrice.toFixed(2)}
                      </StyledUnusualCell>
                      <StyledUnusualCell>{addCommas(u.volume | 0)}</StyledUnusualCell>
                      <StyledUnusualCell>{addCommas(u.openInterest | 0)}</StyledUnusualCell>
                      <StyledUnusualCell>{(u.volume / u.openInterest).toFixed(2)}</StyledUnusualCell>
                      <StyledUnusualCell>{u.volatility.toFixed(2)}%</StyledUnusualCell>
                    </StyledUnusualRow>
                  ))}
                </Tables.Body>
              </Tables.Table>
              <StyleLastUpdatedBlock>Last Updated: {data.unusual.lastUpdatedEst}</StyleLastUpdatedBlock>
            </>
          )}
          {!!data.failures && !!data.failures.failures.length && (
            <>
              <h1>Failures to Deliver</h1>
              <p>
                Each point represents the aggregate net balance of shares that failed to be delivered as of a particular settlement date.
              </p>
              <p>
                Please note that fails-to-deliver can occur for a number of reasons on both long and short sales. Therefore,
                fails-to-deliver are not necessarily the result of short selling, nor evidence of abusive “naked” short selling. For more
                information on short selling and fails-to-deliver, see{' '}
                <a href="http://www.sec.gov/investor/pubs/regsho.htm">Key Points About Regulation SHO</a>,{' '}
                <a href="http://www.sec.gov/divisions/marketreg/mrfaqregsho1204.htm">Division of Market Regulation</a>, and{' '}
                <a href="http://www.sec.gov/rules/final/34-50103.htm">Final Rule: Short Sales</a>.
              </p>
              <FailuresChart data={data.failures.failures} />
              <div style={{ marginTop: '32px' }}>
                <StyleLastUpdatedBlock>
                  Last Updated: {data.failures.failures[data.failures.failures.length - 1].lastUpdatedEst} 12:00 EST
                </StyleLastUpdatedBlock>
              </div>
              {/* <StyledTableContainer>
                <table>
                  <thead>
                    <tr>
                      <th>Symbol (Name)</th>
                      <th>Settlement Date</th>
                      <th>Aggregate Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.failures.failures.map(i => (
                      <tr key={`${i.symbol}_${i.lastUpdatedEst}`}>
                        <StyledTickerNameCell>
                          <span title={i.description}>
                            <b>{data.symbol}</b> <StyledTickerName>({data.name})</StyledTickerName>
                          </span>
                        </StyledTickerNameCell>
                        <StyledFailureCell>{i.lastUpdatedEst}</StyledFailureCell>
                        <StyledFailureCell>{addCommas(i.total)}</StyledFailureCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </StyledTableContainer> */}
            </>
          )}
          <p>
            <br />
          </p>
          {/* <PostsWithFlair
          posts={data.Posts.filter(p => p.Flair === 'DD' && p.Title.indexOf(slug) >= 0)}
          heading="Due Diligences"
          flair="DD"
          ticker={slug}
        />
        <PostsWithFlair posts={data.Posts} ticker={slug} flair="YOLO" heading="YOLOs" />
        <PostsWithFlair posts={data.Posts} ticker={slug} flair="Gain" heading="Gains" />
        <PostsWithFlair posts={data.Posts} ticker={slug} flair="Loss" heading="Losses" /> */}
        </Container>
      </Page>
    </IndexLayout>
  );
}

export default TickerTemplate;
